import axios from 'axios'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Token storage key
const TOKEN_KEY = 'portal_access_token'
const REFRESH_TOKEN_KEY = 'portal_refresh_token'

/**
 * 判断 JWT 是否已过期。
 *
 * BladeX 的 token 有效期 1 小时。原先 isLoggedIn() 只看 localStorage 里有没有这个
 * 字符串，过期的 token 照样算「已登录」—— 于是「我的报名」这类一进页面就发鉴权请求的页面
 * 必然拿到 401，再被下面的拦截器踢去登录页。这就是「有时候点我的报名会跳登录」的根因，
 * 条件是距上次登录超过 1 小时。
 *
 * 解析失败或没有 exp 时【一律不判过期】，交给后端裁决 —— 宁可多发一次请求，
 * 也不能因为解析问题把有效 token 误清掉。
 */
const isJwtExpired = (token) => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return false
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const { exp } = JSON.parse(json)
    if (!exp) return false
    return exp * 1000 <= Date.now()
  } catch {
    return false
  }
}

/*
 * 401 后跳登录页。
 *
 * 用 SPA 跳转而不是 window.location.href：后者整页重载、丢掉 React 状态还闪一下白屏。
 * navigator 由 App 内的桥接组件注册（api.js 不是组件，拿不到 useNavigate），
 * 没注册上时退回硬跳转，保证任何情况下都不会卡在原页面。
 * 带上 redirect 让用户登录完能回到刚才那一页。
 */
let spaNavigator = null
export const setApiNavigator = (fn) => { spaNavigator = fn }

// 同一时刻多个请求一起 401 时只提示一次
let expiredNoticeAt = 0

const redirectToLogin = () => {
  const { pathname, search } = window.location
  const publicPaths = ['/', '/login', '/signup', '/freight-calculator', '/tracking']
  if (publicPaths.includes(pathname)) return

  if (Date.now() - expiredNoticeAt > 3000) {
    expiredNoticeAt = Date.now()
    toast.error('登录已过期，请重新登录')
  }

  const target = `/login?redirect=${encodeURIComponent(pathname + search)}`
  if (spaNavigator) spaNavigator(target, { replace: true })
  else window.location.href = target
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add Bearer Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    // Add tenant and client headers for Spring Blade
    config.headers['Tenant-Id'] = '000000'
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle Spring Blade response format
api.interceptors.response.use(
  (response) => {
    const res = response.data
    // Spring Blade format: { code: 200, success: true, data: xxx, msg: "xxx" }
    if (res.code === 200 || res.success) {
      return res.data !== undefined ? res.data : res
    }
    // Business error
    const message = res.msg || res.message || '请求失败'
    return Promise.reject(new Error(message))
  },
  (error) => {
    let message = '请求失败，请稍后重试'
    
    if (error.response) {
      const { status, data } = error.response
      message = data?.msg || data?.message || message
      
      // Handle authentication errors
      if (status === 401 || message.includes('请先登录')) {
        // Clear tokens
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        redirectToLogin()
      } else if(status === 400){
        toast.error(message)
      }
    }
    
    return Promise.reject(new Error(message))
  }
)

// Token management utilities
export const tokenUtils = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
  // 过期的 token 一律当未登录，并就地清掉 —— 否则后续每个鉴权请求都注定 401
  isLoggedIn: () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return false
    if (isJwtExpired(token)) {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      return false
    }
    return true
  },
}

// Generic API methods
export const get = (url, params = {}) => api.get(url, { params })
export const post = (url, data = {}) => api.post(url, data)
export const put = (url, data = {}) => api.put(url, data)
export const del = (url) => api.delete(url)

export default api
